import { memo, useMemo, useState, useEffect, useCallback } from 'react';
import * as d3 from 'd3';
import { ChartWrapper } from '../shared/ChartWrapper';
import { useOverviewData } from './useOverviewData';
import { ChevronRight } from 'lucide-react';

// Result type → output/outcome level mapping
const TYPE_LEVELS = {
  'Knowledge product': 'Output',
  'Innovation development': 'Output',
  'Capacity sharing for development': 'Output',
  'Other output': 'Output',
  'Innovation use': 'Outcome',
  'Policy change': 'Outcome',
  'Other outcome': 'Outcome',
  'Innovation Package': 'Output',
};

const OUTPUT_COLORS = ['#006837', '#3B9B61', '#6ABD70', '#A1D99B', '#C7E9C0'];
const OUTCOME_COLORS = ['#F37021', '#FDB913', '#FDCF58', '#FFE08A', '#FFF3C4'];

const WIDTH = 600;
const HEIGHT = 380;

/** Compute d3 treemap layout on a hierarchy data object */
function layoutTreemap(data, groupLabels = false) {
  const root = d3.hierarchy(data)
    .sum(d => d.value || 0)
    .sort((a, b) => (b.value || 0) - (a.value || 0));
  d3.treemap()
    .size([WIDTH, HEIGHT])
    .paddingInner(3)
    .paddingOuter(4)
    .paddingTop(groupLabels ? 20 : 4)
    .round(true)(root);
  return root;
}

/** Aggregate filteredResults into type-level hierarchy grouped by Output/Outcome */
function buildTypeHierarchy(results) {
  const groups = { Outputs: {}, Outcomes: {} };
  for (const r of results) {
    if (!r.type) continue;
    const level = TYPE_LEVELS[r.type] || 'Output';
    const bucket = level === 'Outcome' ? groups.Outcomes : groups.Outputs;
    if (!bucket[r.type]) bucket[r.type] = { w1w2: 0, w3: 0 };
    r.fundingType === 'W3Bilateral' ? bucket[r.type].w3++ : bucket[r.type].w1w2++;
  }
  const children = [];
  for (const [name, types] of Object.entries(groups)) {
    const typeChildren = Object.entries(types).map(([typeName, c]) => ({
      name: typeName, w1w2: c.w1w2, w3: c.w3, value: c.w1w2 + c.w3, level: name === 'Outcomes' ? 'Outcome' : 'Output',
    }));
    if (typeChildren.length > 0) children.push({ name, children: typeChildren });
  }
  return { name: 'All Results', children };
}

/** Build a 2-cell hierarchy showing W1/W2 vs W3 for a single type */
function buildFundingSplitHierarchy(typeName, w1w2, w3) {
  const children = [];
  if (w1w2 > 0) children.push({ name: 'W1/W2', value: w1w2, w1w2, w3: 0, fundingKey: 'w1w2' });
  if (w3 > 0) children.push({ name: 'W3/Bilateral', value: w3, w1w2: 0, w3, fundingKey: 'w3' });
  return { name: typeName, children };
}

/** Extract a single group (Outputs or Outcomes) as a standalone hierarchy */
function buildGroupHierarchy(typeData, groupName) {
  const group = typeData.children.find(c => c.name === groupName);
  if (!group) return null;
  return { name: groupName, children: group.children.map(c => ({ ...c })) };
}

export const ResultTreemapChart = memo(function ResultTreemapChart() {
  const { filteredResults } = useOverviewData();
  const [tooltip, setTooltip] = useState(null);
  const [zoomPath, setZoomPath] = useState([]); // [{name, type: 'group'|'type', w1w2?, w3?}]
  const [animated, setAnimated] = useState(false);

  // Build base type-level data
  const typeData = useMemo(() => buildTypeHierarchy(filteredResults || []), [filteredResults]);

  // Determine zoom mode from path
  const zoomMode = zoomPath.length === 0 ? 'root'
    : zoomPath[zoomPath.length - 1].type === 'type' ? 'type'
    : 'group';

  // Compute treemap layout for current zoom level
  const { leaves, groups, colorMap, chartData } = useMemo(() => {
    let data;
    if (zoomMode === 'type') {
      const last = zoomPath[zoomPath.length - 1];
      data = buildFundingSplitHierarchy(last.name, last.w1w2 || 0, last.w3 || 0);
    } else if (zoomMode === 'group') {
      data = buildGroupHierarchy(typeData, zoomPath[0].name);
      if (!data) data = typeData; // fallback
    } else {
      data = typeData;
    }

    if (!data.children || data.children.length === 0) {
      return { leaves: [], groups: [], colorMap: {}, chartData: [] };
    }

    const root = layoutTreemap(data, zoomMode === 'root');
    const lv = root.leaves();
    const gr = zoomMode === 'root' ? (root.children || []) : [];

    // Assign colors
    const cm = {};
    if (zoomMode === 'type') {
      // Funding split view: find the parent type's level to pick base color
      const last = zoomPath[zoomPath.length - 1];
      const typeLevel = TYPE_LEVELS[last.name] || 'Output';
      const baseColor = typeLevel === 'Outcome' ? OUTCOME_COLORS[0] : OUTPUT_COLORS[0];
      cm['W1/W2'] = baseColor;
      cm['W3/Bilateral'] = d3.color(baseColor).darker(0.7).toString();
    } else {
      let outputIdx = 0, outcomeIdx = 0;
      lv.forEach(leaf => {
        const level = leaf.data.level || (leaf.parent?.data.name === 'Outcomes' ? 'Outcome' : 'Output');
        if (level === 'Outcome') {
          cm[leaf.data.name] = OUTCOME_COLORS[outcomeIdx++ % OUTCOME_COLORS.length];
        } else {
          cm[leaf.data.name] = OUTPUT_COLORS[outputIdx++ % OUTPUT_COLORS.length];
        }
      });
    }

    const cd = lv.map(l => ({
      'Result Type': l.data.name,
      'Total': l.value,
      'W1/W2': l.data.w1w2 || 0,
      'W3/Bilateral': l.data.w3 || 0,
    }));

    return { leaves: lv, groups: gr, colorMap: cm, chartData: cd };
  }, [typeData, zoomMode, zoomPath, filteredResults]);

  // Re-trigger entry animation on zoom change
  const zoomKey = zoomPath.map(z => z.name).join('/');
  useEffect(() => {
    setAnimated(false);
    const timer = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(timer);
  }, [zoomKey]);

  // Initial mount animation
  useEffect(() => {
    const timer = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const handleCellClick = useCallback((leaf) => {
    setZoomPath(prev => [
      ...prev,
      { name: leaf.data.name, type: 'type', w1w2: leaf.data.w1w2 || 0, w3: leaf.data.w3 || 0 },
    ]);
    setTooltip(null);
  }, []);

  const handleGroupClick = useCallback((group) => {
    setZoomPath([{ name: group.data.name, type: 'group' }]);
    setTooltip(null);
  }, []);

  const handleBreadcrumb = useCallback((index) => {
    setZoomPath(prev => index < 0 ? [] : prev.slice(0, index + 1));
    setTooltip(null);
  }, []);

  const handleMouseMove = useCallback((node, e) => {
    setTooltip({ x: e.clientX, y: e.clientY, data: node.data, value: node.value });
  }, []);

  const handleMouseLeave = useCallback(() => setTooltip(null), []);

  if (!leaves.length) return null;

  const isZoomed = zoomPath.length > 0;

  return (
    <ChartWrapper
      title="Result Composition"
      info="Treemap showing result types sized by count. Click to drill down. Each cell shows W1/W2 (lighter) vs W3/Bilateral (darker) funding split."
      chartData={chartData}
      sourceResults={filteredResults}
      exportFilename="result-treemap"
    >
      {/* Breadcrumb */}
      {isZoomed && (
        <div className="flex items-center gap-1 text-xs text-[var(--text-secondary)] mb-2 px-1">
          <button
            onClick={() => handleBreadcrumb(-1)}
            className="hover:text-[var(--text-primary)] hover:underline cursor-pointer"
          >
            All Results
          </button>
          {zoomPath.map((z, i) => (
            <span key={i} className="flex items-center gap-1">
              <ChevronRight className="w-3 h-3" />
              {i === zoomPath.length - 1 ? (
                <span className="font-medium text-[var(--text-primary)]">{z.name}</span>
              ) : (
                <button
                  onClick={() => handleBreadcrumb(i)}
                  className="hover:text-[var(--text-primary)] hover:underline cursor-pointer"
                >
                  {z.name}
                </button>
              )}
            </span>
          ))}
        </div>
      )}

      <div className="relative flex justify-center">
        <svg viewBox={`0 0 ${WIDTH} ${HEIGHT}`} className="w-full" style={{ maxHeight: '380px' }}>
          <defs>
            <filter id="tm-shadow">
              <feDropShadow dx="0" dy="1" stdDeviation="1.5" floodOpacity="0.15" />
            </filter>
          </defs>

          {/* Group outlines + clickable headers (root view only) */}
          {groups.map(group => {
            const gw = group.x1 - group.x0;
            return (
              <g key={`group-${group.data.name}`}>
                <rect
                  x={group.x0} y={group.y0} width={gw} height={group.y1 - group.y0}
                  rx="6" fill="none" stroke="var(--border-primary)" strokeWidth="0.5"
                  style={{ opacity: animated ? 0.4 : 0, transition: 'opacity 0.3s' }}
                />
                {/* Clickable header area */}
                <rect
                  x={group.x0} y={group.y0} width={gw} height={18}
                  fill="transparent" style={{ cursor: 'pointer' }}
                  onClick={() => handleGroupClick(group)}
                />
                <text
                  x={group.x0 + 8} y={group.y0 + 13}
                  fontSize="10" fontWeight="600" fill="var(--text-tertiary)"
                  style={{ pointerEvents: 'none', opacity: animated ? 0.7 : 0, transition: 'opacity 0.3s' }}
                >
                  {group.data.name} ({group.value.toLocaleString()})
                </text>
              </g>
            );
          })}

          {/* Leaf cells with W1/W2 vs W3 split */}
          {leaves.map((leaf, i) => {
            const w = leaf.x1 - leaf.x0;
            const h = leaf.y1 - leaf.y0;
            const baseColor = colorMap[leaf.data.name] || '#999';
            const darkerColor = d3.color(baseColor).darker(0.7).toString();
            const w1w2 = leaf.data.w1w2 || 0;
            const w3 = leaf.data.w3 || 0;
            const total = w1w2 + w3;
            const w1w2Frac = total > 0 ? w1w2 / total : 1;

            const fontSize = w > 100 ? 11 : 9;
            const charWidth = fontSize === 11 ? 6.5 : 5.5;
            const maxChars = Math.floor((w - 16) / charWidth);
            const showLabel = w > 32 && h > 22 && maxChars >= 3;
            const showValue = w > 36 && h > 38;
            const showSplit = w > 55 && h > 50 && zoomMode !== 'type';
            const canDrill = zoomMode !== 'type';

            return (
              <g
                key={leaf.data.name}
                onMouseMove={(e) => handleMouseMove(leaf, e)}
                onMouseLeave={handleMouseLeave}
                onClick={canDrill ? () => handleCellClick(leaf) : undefined}
                style={{
                  cursor: canDrill ? 'pointer' : 'default',
                  transform: animated ? 'scale(1)' : 'scale(0)',
                  transformOrigin: `${leaf.x0 + w / 2}px ${leaf.y0 + h / 2}px`,
                  transition: `transform 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) ${i * 40}ms`,
                }}
              >
                {/* Base rect — W1/W2 color, provides shape + shadow */}
                <rect
                  x={leaf.x0} y={leaf.y0} width={w} height={h}
                  rx="4" fill={baseColor} filter="url(#tm-shadow)"
                />
                {/* W3 overlay on right side */}
                {w3 > 0 && (
                  <rect
                    x={leaf.x0 + w * w1w2Frac} y={leaf.y0}
                    width={w * (1 - w1w2Frac)} height={h}
                    fill={darkerColor}
                  />
                )}
                {/* Divider line between W1/W2 and W3 */}
                {w3 > 0 && w1w2 > 0 && (
                  <line
                    x1={leaf.x0 + w * w1w2Frac} y1={leaf.y0 + 1}
                    x2={leaf.x0 + w * w1w2Frac} y2={leaf.y0 + h - 1}
                    stroke="rgba(255,255,255,0.25)" strokeWidth="1"
                  />
                )}
                {/* Label */}
                {showLabel && (
                  <text
                    x={leaf.x0 + w / 2}
                    y={leaf.y0 + h / 2 + (showValue ? -6 : 0) + (showSplit ? -4 : 0)}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize={fontSize} fontWeight="600" fill="white"
                    style={{ pointerEvents: 'none', opacity: 0.95 }}
                  >
                    {leaf.data.name.length > maxChars
                      ? leaf.data.name.slice(0, maxChars - 1) + '\u2026'
                      : leaf.data.name}
                  </text>
                )}
                {/* Value */}
                {showValue && (
                  <text
                    x={leaf.x0 + w / 2}
                    y={leaf.y0 + h / 2 + (showSplit ? 4 : 10)}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize="10" fill="white"
                    style={{ pointerEvents: 'none', opacity: 0.7 }}
                  >
                    {leaf.value.toLocaleString()}
                  </text>
                )}
                {/* W1/W2 · W3 counts */}
                {showSplit && (
                  <text
                    x={leaf.x0 + w / 2}
                    y={leaf.y0 + h / 2 + 16}
                    textAnchor="middle" dominantBaseline="middle"
                    fontSize="8" fill="white"
                    style={{ pointerEvents: 'none', opacity: 0.5 }}
                  >
                    W1/W2: {w1w2} · W3: {w3}
                  </text>
                )}
                {/* Drill indicator */}
                {canDrill && w > 40 && h > 20 && (
                  <text
                    x={leaf.x1 - 10} y={leaf.y0 + 12}
                    fontSize="9" fill="white"
                    style={{ pointerEvents: 'none', opacity: 0.4 }}
                  >
                    ▸
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {/* Tooltip */}
        {tooltip && (
          <div
            className="fixed bg-gray-900 text-white text-xs rounded-lg px-3 py-2 pointer-events-none shadow-lg z-50"
            style={{ left: tooltip.x + 12, top: tooltip.y - 28 }}
          >
            <div className="font-semibold mb-1">{tooltip.data.name}</div>
            <div>Total: {tooltip.value.toLocaleString()}</div>
            {tooltip.data.w1w2 != null && <div>W1/W2: {tooltip.data.w1w2.toLocaleString()}</div>}
            {tooltip.data.w3 != null && <div>W3/Bilateral: {tooltip.data.w3.toLocaleString()}</div>}
            {tooltip.data.level && <div className="text-gray-400 mt-0.5">{tooltip.data.level}</div>}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap justify-center gap-x-5 gap-y-1 mt-3">
        {zoomMode !== 'type' && (
          <>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: OUTPUT_COLORS[0] }} />
              <span className="text-xs text-[var(--text-secondary)]">Outputs</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: OUTCOME_COLORS[0] }} />
              <span className="text-xs text-[var(--text-secondary)]">Outcomes</span>
            </div>
          </>
        )}
        <div className="flex items-center gap-2">
          <div className="flex">
            <div className="w-2.5 h-3 rounded-l-sm" style={{ backgroundColor: OUTPUT_COLORS[0] }} />
            <div className="w-2.5 h-3 rounded-r-sm" style={{ backgroundColor: d3.color(OUTPUT_COLORS[0]).darker(0.7).toString() }} />
          </div>
          <div className="flex">
            <div className="w-2.5 h-3 rounded-l-sm" style={{ backgroundColor: OUTCOME_COLORS[0] }} />
            <div className="w-2.5 h-3 rounded-r-sm" style={{ backgroundColor: d3.color(OUTCOME_COLORS[0]).darker(0.7).toString() }} />
          </div>
          <span className="text-xs text-[var(--text-secondary)]">W1/W2 · W3/Bilateral</span>
        </div>
      </div>
    </ChartWrapper>
  );
});
